import React, { useState, useCallback, useEffect } from 'react'
import {
  Package,
  ShoppingCart,
  ShoppingBag,
  ArrowRightLeft,
  Settings2,
  TrendingUp,
  TrendingDown,
  Layers,
  Calendar,
  Building2,
  Eye,
  X,
  Barcode,
  User,
  Printer
} from 'lucide-react'
import { toast } from 'sonner'

// ── Componentes Globales ──────────────────────────────────────────────────────
import { Button } from '../components/ui/button'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'

import { ExportModal, type ExportFormat } from '@/app/components/ui/ExportModal'

// ── Servicio ──────────────────────────────────────────────────────────────────
import {
  inventoryTransfersService,
  InventoryTransferType,
} from '../services/transfers.service'

import {
  type TransferExportFormat,
  transferService
} from '@/app/services/transfer.service'

// ── Stores ────────────────────────────────────────────────────────────────────
import { useAuthStore } from '../store/auth/auth.store'

// ─────────────────────────────────────────────────────────────────────────────
// Tipos unificados
// ─────────────────────────────────────────────────────────────────────────────

interface UnifiedTransfer {
  id: string
  raw_id: number
  type: InventoryTransferType
  date: string
  origin_sucursal_name: string
  destination_sucursal_name: string
  user_name: string
  user_email: string
  status: string
  items_count: number
  notes: string
  original: any
}

function mapRequestToUnified(r: any): UnifiedTransfer {
  return {
    id: `REQ-${r.id}`,
    raw_id: r.id,
    type: 'transfer_requested',
    date: r.requested_at || r.created_at,
    origin_sucursal_name: r.origin_sucursal?.name || `Sucursal ${r.origin_sucursal_id}`,
    destination_sucursal_name: r.destination_sucursal?.name || `Sucursal ${r.destination_sucursal_id}`,
    user_name: r.requested_by?.name || r.requestedBy?.name || 'Sistema',
    user_email: r.requested_by?.email || r.requestedBy?.email || '',
    status: r.status,
    items_count: r.items?.length || 0,
    notes: r.notes || '',
    original: r,
  }
}

function mapTransferToUnified(t: any, forcedType?: InventoryTransferType): UnifiedTransfer {
  let type = forcedType
  if (!type) {
    type = t.status === 'COMPLETED' ? 'transfer_received' : 'transfer_sent'
  }

  const user = type === 'transfer_received' && t.receipt?.received_by ? t.receipt.received_by : t.sent_by || t.sentBy

  return {
    id: `TRF-${t.id}`,
    raw_id: t.id,
    type,
    date: type === 'transfer_received' && t.received_at ? t.received_at : (t.sent_at || t.created_at),
    origin_sucursal_name: t.origin_sucursal?.name || `Sucursal ${t.origin_sucursal_id}`,
    destination_sucursal_name: t.destination_sucursal?.name || `Sucursal ${t.destination_sucursal_id}`,
    user_name: user?.name || 'Sistema',
    user_email: user?.email || '',
    status: t.status,
    items_count: t.items?.length || 0,
    notes: t.notes || '',
    original: t,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuración de tipos de movimiento
// ─────────────────────────────────────────────────────────────────────────────

interface MovementConfig {
  label: string
  shortLabel: string
  bgColor: string
  textColor: string
  ringColor: string
  Icon: React.ElementType
}

const TRANSFER_CONFIG: Record<InventoryTransferType, MovementConfig> = {
  transfer_sent: {
    label: 'Envío de Transferencia',
    shortLabel: 'Envío',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    ringColor: 'ring-orange-200',
    Icon: ShoppingBag,
  },
  transfer_requested: {
    label: 'Solicitud de Transferencia',
    shortLabel: 'Solicitud',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    ringColor: 'ring-green-200',
    Icon: ShoppingCart,
  },
  transfer_received: {
    label: 'Recepción de Transferencia',
    shortLabel: 'Recepción',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    ringColor: 'ring-purple-200',
    Icon: Package,
  }
}

const DEFAULT_TRANSFER_CONFIG: MovementConfig = {
  label: 'Transferencia No Identificada',
  shortLabel: 'Desconocido',
  bgColor: 'bg-slate-100',
  textColor: 'text-slate-600',
  ringColor: 'ring-slate-200',
  Icon: Package,
}

function getMovementConfig(type: string): MovementConfig {
  return TRANSFER_CONFIG[type as InventoryTransferType] ?? DEFAULT_TRANSFER_CONFIG
}

const STATUS_LABELS: Record<string, { label: string, color: string }> = {
  PENDING: { label: 'Pendiente', color: 'bg-slate-100 text-slate-700' },
  APPROVED: { label: 'Aprobada', color: 'bg-blue-100 text-blue-700' },
  SHIPPED: { label: 'Enviada', color: 'bg-orange-100 text-orange-700' },
  PENDING_RECEPTION: { label: 'En Tránsito', color: 'bg-orange-100 text-orange-700' },
  COMPLETED: { label: 'Completada', color: 'bg-emerald-100 text-emerald-700' },
  CANCELLED: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
  REJECTED: { label: 'Rechazada', color: 'bg-red-100 text-red-700' },
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de fecha
// ─────────────────────────────────────────────────────────────────────────────

function todayISO() {
  return new Date().toLocaleDateString('sv-SE')
}

function yesterdayISO() {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toLocaleDateString('sv-SE')
}

function formatDate(raw: string): string {
  try {
    const d = new Date(raw)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    const isYesterday = d.toDateString() === yesterday.toDateString()
    const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
    if (isToday) return `Hoy · ${time}`
    if (isYesterday) return `Ayer · ${time}`
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) + ` · ${time}`
  } catch {
    return raw
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de exportación
// ─────────────────────────────────────────────────────────────────────────────

function getApiMessage(error: unknown, fallback: string) {
  const responseError = error as { response?: { data?: { message?: string } } }
  return responseError.response?.data?.message || fallback
}

function filenameFromDisposition(disposition: string | undefined, fallback: string) {
  if (!disposition) return fallback
  const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1].replace(/"/g, ''))
  }
  const match = disposition.match(/filename="?([^";]+)"?/i)
  return match?.[1] || fallback
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

// ─────────────────────────────────────────────────────────────────────────────
// Selección para exportación
// ─────────────────────────────────────────────────────────────────────────────
type TransferExportSelection = {
  transferRequestIds?: number[]
  inventoryTransferIds?: number[]
} | null

// ─────────────────────────────────────────────────────────────────────────────
// Modal de Detalle
// ─────────────────────────────────────────────────────────────────────────────

function TransferDetailModal({
  transfer,
  onClose,
}: {
  transfer: UnifiedTransfer | null
  onClose: () => void
}) {
  if (!transfer) return null
  const cfg = getMovementConfig(transfer.type)
  const statusCfg = STATUS_LABELS[transfer.status] || { label: transfer.status, color: 'bg-slate-100 text-slate-700' }

  return (
    <Dialog open={!!transfer} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl bg-popover text-popover-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <cfg.Icon className="h-5 w-5" />
            Detalle de {cfg.label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tipo + dirección */}
          <div className="flex items-center gap-3 rounded-lg border border-popover-foreground/10 bg-black/10 p-4 shadow-sm">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${cfg.bgColor}`}>
              <cfg.Icon className={`h-5 w-5 ${cfg.textColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-popover-foreground">{cfg.label} <span className="text-popover-foreground/60 font-mono text-sm ml-2">#{transfer.id}</span></p>
              <p className="text-xs text-popover-foreground/60">{formatDate(transfer.date)}</p>
            </div>
            <div className="ml-auto shrink-0">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${statusCfg.color}`}>
                {statusCfg.label}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Ruta */}
            <section className="rounded-lg border border-popover-foreground/10 bg-black/10 p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-popover-foreground/60">Ruta de Transferencia</h3>
              <div className="flex gap-4 flex-col">
                <div className="flex-1 w-fit">
                  <p className="text-xs text-popover-foreground/60">Origen</p>
                  <p className="font-medium text-popover-foreground text-sm flex items-center gap-1.5 mt-1">
                    <Building2 className="h-4 w-4 text-popover-foreground/50" />
                    {transfer.origin_sucursal_name}
                  </p>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-popover-foreground/60">Destino</p>
                  <p className="font-medium text-popover-foreground text-sm flex items-center gap-1.5 mt-1">
                    <Building2 className="h-4 w-4 text-popover-foreground/50" />
                    {transfer.destination_sucursal_name}
                  </p>
                </div>
              </div>
            </section>

            {/* Usuario y Registro */}
            <section className="rounded-lg border border-popover-foreground/10 bg-black/10 p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-popover-foreground/60">Trazabilidad</h3>
              <dl className="grid grid-cols-1 gap-y-3 text-sm">
                <div>
                  <dt className="text-popover-foreground/60 text-xs uppercase tracking-wider font-semibold mb-1">Registrado por</dt>
                  <dd className="flex items-center gap-1.5 font-medium text-popover-foreground">
                    <User className="h-4 w-4 text-popover-foreground/50" />
                    {transfer.user_name}
                  </dd>
                  {transfer.user_email && (
                    <dd className="text-xs text-popover-foreground/60 ml-5.5 pl-1">{transfer.user_email}</dd>
                  )}
                </div>
                {transfer.notes && (
                  <div>
                    <dt className="text-popover-foreground/60 text-xs uppercase tracking-wider font-semibold mb-1">Notas</dt>
                    <dd className="text-popover-foreground/80 text-sm italic border-l-2 border-popover-foreground/20 pl-2">{transfer.notes}</dd>
                  </div>
                )}
              </dl>
            </section>
          </div>

          {/* Productos */}
          <section className="rounded-lg border border-popover-foreground/10 bg-transparent p-0 overflow-hidden">
            <div className="bg-black/10 p-3 border-b border-popover-foreground/10">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-popover-foreground/60">Artículos ({transfer.items_count})</h3>
            </div>
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-black/5 sticky top-0 border-b border-popover-foreground/10 shadow-sm z-10">
                  <tr>
                    <th className="px-4 py-2 font-medium text-popover-foreground/70">Producto</th>
                    <th className="px-4 py-2 font-medium text-popover-foreground/70">Código</th>
                    <th className="px-4 py-2 font-medium text-popover-foreground/70 text-right">Cantidad</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-popover-foreground/10">
                  {(transfer.original.items || []).map((item: any, idx: number) => {
                    const product = item.product || {};
                    const qty = item.requested_quantity || item.approved_quantity || item.quantity || item.received_quantity || 0;
                    return (
                      <tr key={idx} className="hover:bg-black/5">
                        <td className="px-4 py-2 font-medium text-popover-foreground">{product.name || `Producto #${item.product_id}`}</td>
                        <td className="px-4 py-2 font-mono text-xs text-popover-foreground/60">{product.barcode || '-'}</td>
                        <td className="px-4 py-2 text-right font-bold text-popover-foreground">{qty}</td>
                      </tr>
                    )
                  })}
                  {(!transfer.original.items || transfer.original.items.length === 0) && (
                    <tr>
                      <td colSpan={3} className="px-4 py-4 text-center text-popover-foreground/50 italic">No hay artículos detallados</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Paginación local
// ─────────────────────────────────────────────────────────────────────────────

interface PaginationState {
  currentPage: number
  total: number
  perPage: number
  lastPage: number
  from: number
  to: number
}

const INITIAL_PAGINATION: PaginationState = {
  currentPage: 1, total: 0, perPage: 15, lastPage: 1, from: 0, to: 0,
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente Principal
// ─────────────────────────────────────────────────────────────────────────────

export function Transfers() {
  const { activeBranchId } = useAuthStore()

  // ── Estado de datos ────────────────────────────────────────────────────────
  const [transfers, setTransfers] = useState<UnifiedTransfer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION)
  const [selectedTransfer, setSelectedTransfer] = useState<UnifiedTransfer | null>(null)

  // ── Filtros ────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<InventoryTransferType | 'all'>('all')
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')
  /** ID de sucursal origen — string vacío = todas */
  const [filterOriginSucursalId, setFilterOriginSucursalId] = useState('')
  /** ID de sucursal destino — string vacío = todas */
  const [filterDestinationSucursalId, setFilterDestinationSucursalId] = useState('')

  // Export Modal State
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [exportSelection, setExportSelection] = useState<TransferExportSelection>(null)

  // ── Acceso rápido de fecha ────────────────────────────────────────────────
  const setQuickDate = (range: 'today' | 'yesterday') => {
    if (range === 'today') {
      const t = todayISO()
      setFilterFrom(t)
      setFilterTo(t)
    } else {
      const y = yesterdayISO()
      setFilterFrom(y)
      setFilterTo(y)
    }
  }

  const clearDates = () => {
    setFilterFrom('')
    setFilterTo('')
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchTransfers = useCallback(async (page = 1) => {
    setIsLoading(true)
    try {
      const params: any = {
        page,
        per_page: 15,
        search: searchQuery || undefined,
        from: filterFrom || undefined,
        to: filterTo || undefined,
      }

      if (filterOriginSucursalId) {
        params.origin_sucursal_id = Number(filterOriginSucursalId)
      }
      if (filterDestinationSucursalId) {
        params.destination_sucursal_id = Number(filterDestinationSucursalId)
      }

      let results: UnifiedTransfer[] = [];
      let total = 0;
      let perPage = 15;
      let from = 0;
      let to = 0;
      let lastPage = 1;

      if (filterType === 'transfer_requested') {
        const res = await inventoryTransfersService.getTransferRequests(params)
        results = (res.data || []).map(mapRequestToUnified)
        total = res.total || 0; perPage = res.per_page || 15; lastPage = res.last_page || 1; from = res.from || 0; to = res.to || 0;
      } else if (filterType === 'transfer_sent') {
        const res = await inventoryTransfersService.getTransfers(params)
        results = (res.data || []).map(t => mapTransferToUnified(t, 'transfer_sent'))
        total = res.total || 0; perPage = res.per_page || 15; lastPage = res.last_page || 1; from = res.from || 0; to = res.to || 0;
      } else if (filterType === 'transfer_received') {
        const res = await inventoryTransfersService.getTransfers({ ...params, status: 'COMPLETED' })
        results = (res.data || []).map(t => mapTransferToUnified(t, 'transfer_received'))
        total = res.total || 0; perPage = res.per_page || 15; lastPage = res.last_page || 1; from = res.from || 0; to = res.to || 0;
      } else {
        // all: consulta ambas APIs con la misma página
        const perPageEach = 8
        const [resReq, resTrans] = await Promise.all([
          inventoryTransfersService.getTransferRequests({ ...params, per_page: perPageEach }),
          inventoryTransfersService.getTransfers({ ...params, per_page: perPageEach })
        ])

        const reqUnified = (resReq.data || []).map(mapRequestToUnified)
        const transUnified = (resTrans.data || []).map(t => mapTransferToUnified(t))

        results = [...reqUnified, ...transUnified].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        const totalReq = resReq.total || 0
        const totalTrans = resTrans.total || 0
        total = totalReq + totalTrans
        perPage = perPageEach * 2
        lastPage = Math.ceil(total / perPage) || 1
        from = ((page - 1) * perPage) + 1
        to = Math.min(page * perPage, total)
      }

      setTransfers(results)
      setPagination({ currentPage: page, total, perPage, lastPage, from, to })

    } catch {
      toast.error('Error al cargar el historial de transferencias')
    } finally {
      setIsLoading(false)
    }
  }, [searchQuery, filterType, filterFrom, filterTo, filterOriginSucursalId, filterDestinationSucursalId, activeBranchId])

  useEffect(() => {
    fetchTransfers(1)
  }, [fetchTransfers])

  const handlePageChange = (page: number) => fetchTransfers(page)

  const hasActiveFilters = !!(
    searchQuery || filterType !== 'all' || filterFrom || filterTo || filterOriginSucursalId || filterDestinationSucursalId
  )

  const resetFilters = () => {
    setSearchQuery('')
    setFilterType('all')
    setFilterFrom('')
    setFilterTo('')
    setFilterOriginSucursalId('')
    setFilterDestinationSucursalId('')
  }

  const openExportModal = (selection: TransferExportSelection = null) => {
    setExportSelection(selection)
    setIsExportModalOpen(true)
  }

  const handleExport = async (format: ExportFormat) => {
    if (!activeBranchId) {
      toast.error('Selecciona una sucursal para exportar transferencias')
      return
    }

    if (format === 'word') {
      toast.error('Formato no disponible para transferencias')
      return
    }

    const transferRequestIds = exportSelection?.transferRequestIds ?? []
    const inventoryTransferIds = exportSelection?.inventoryTransferIds ?? []

    try {
      const response = await transferService.exportTransferDetails({
        context: 'history',
        format: (format === 'excel' ? 'xlsx' : format) as TransferExportFormat,
        sucursal_id: activeBranchId,
        transfer_request_ids: transferRequestIds,
        inventory_transfer_ids: inventoryTransferIds,
      })

      if (format === 'pdf') {
        const url = URL.createObjectURL(response.data)
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = url
        document.body.appendChild(iframe)
        iframe.onload = () => {
          setTimeout(() => {
            iframe.contentWindow?.print()
          }, 100)
        }
        toast.success('Preparando documento para imprimir')
      } else {
        const contentDisposition = response.headers['content-disposition'] as string | undefined
        const filename = filenameFromDisposition(
          contentDisposition,
          `transferencias-historial.${format === 'excel' ? 'xlsx' : format}`,
        )
        downloadBlob(response.data, filename)
        toast.success('Exportacion generada correctamente')
      }
    } catch (error) {
      toast.error(getApiMessage(error, 'No se pudo exportar transferencias'))
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6 w-full max-w-full min-w-0">
      {/* ── Header ── */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">Historial de Transferencias</h1>
        <p className="text-sm text-muted-foreground">
          Listado completo de envíos, solicitudes y recepciones.
        </p>
      </div>

      {/* ── Panel de Filtros ── */}
      <GlobalFilterBar
        show={['transferType', 'originBranch', 'destinationBranch', 'dates']}
        dateShortcuts={['today', 'yesterday']}
        values={{
          transferType: filterType === 'all' ? '' : filterType,
          originBranch: filterOriginSucursalId,
          destinationBranch: filterDestinationSucursalId,
          startDate: filterFrom,
          endDate: filterTo,
        }}
        onChange={(changes) => {
          if (changes.transferType !== undefined) setFilterType(changes.transferType ? (changes.transferType as InventoryTransferType) : 'all')
          if (changes.originBranch !== undefined) setFilterOriginSucursalId(changes.originBranch)
          if (changes.destinationBranch !== undefined) setFilterDestinationSucursalId(changes.destinationBranch)
          if (changes.startDate !== undefined) setFilterFrom(changes.startDate)
          if (changes.endDate !== undefined) setFilterTo(changes.endDate)
        }}
        customOptions={{
          transferTypeOptions: (Object.entries(TRANSFER_CONFIG) as [InventoryTransferType, MovementConfig][]).map(
            ([key, cfg]) => ({ value: key, label: cfg.label })
          ),
          originBranchPlaceholder: "Sucursal Origen",
          destinationBranchPlaceholder: "Sucursal Destino",
        }}
        onClearFilters={hasActiveFilters ? resetFilters : undefined}
        hasActiveFilters={hasActiveFilters}
      />


      {/* ── Tabla ── */}
      {isLoading ? (
        <TableSkeleton
          headers={['Fecha', 'ID', 'Tipo', 'Origen', 'Destino', 'Artículos', 'Estado', 'Usuario', 'Acciones']}
          columns={['text', 'text', 'badge', 'text', 'text', 'text', 'badge', 'text-two-lines', 'actions']}
          rows={8}
        />
      ) : (
        <div className="w-full overflow-hidden rounded-xl border border-border bg-table-bg shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-border bg-table-header-bg">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Origen</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Destino</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground text-center">Artículos</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Usuario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {transfers.length > 0 ? (
                  transfers.map((mov) => {
                    const cfg = getMovementConfig(mov.type)
                    const statusCfg = STATUS_LABELS[mov.status] || { label: mov.status, color: 'bg-slate-100 text-slate-700' }

                    return (
                      <tr
                        key={mov.id}
                        className="group transition-colors hover:bg-table-row-hover"
                      >
                        {/* Fecha */}
                        <td className="px-4 py-3 text-sm text-table-foreground/70">
                          {formatDate(mov.date)}
                        </td>

                        {/* ID */}
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs font-semibold text-table-foreground bg-table-foreground/10 px-2 py-1 rounded-md">{mov.id}</span>
                        </td>

                        {/* Tipo de movimiento */}
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${cfg.bgColor} ${cfg.textColor}`}
                          >
                            <cfg.Icon className="h-3 w-3" />
                            {cfg.shortLabel}
                          </span>
                        </td>

                        {/* Origen */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary">
                              <Building2 className="h-5 w-5" />
                            </div>
                            <span className="text-sm text-table-foreground font-semibold">
                              {mov.origin_sucursal_name}
                            </span>
                          </div>
                        </td>

                        {/* Destino */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary">
                              <Building2 className="h-5 w-5" />
                            </div>
                            <span className="text-sm text-table-foreground font-semibold">
                              {mov.destination_sucursal_name}
                            </span>
                          </div>
                        </td>

                        {/* Cantidad de articulos */}
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center min-w-[2rem] h-6 rounded-md bg-table-foreground/10 text-sm font-bold text-table-foreground px-2">
                            {mov.items_count}
                          </span>
                        </td>

                        {/* Estado */}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${statusCfg.color}`}>
                            {statusCfg.label}
                          </span>
                        </td>

                        {/* Usuario */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-table-foreground/10 text-table-foreground/70">
                              <User className="h-4 w-4" />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm text-table-foreground font-medium table-cell-truncate" title={mov.user_name}>{mov.user_name}</span>
                              {mov.user_email && <span className="text-[10px] text-table-foreground/70">{mov.user_email}</span>}
                            </div>
                          </div>
                        </td>

                        {/* Acción */}
                        <td className="px-6 py-4 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedTransfer(mov)}
                            className="rounded p-1.5 text-table-foreground/70 transition-colors hover:bg-table-foreground/10 hover:text-table-foreground"
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              const selection: TransferExportSelection = mov.type === 'transfer_requested'
                                ? { transferRequestIds: [mov.raw_id], inventoryTransferIds: [] }
                                : { transferRequestIds: [], inventoryTransferIds: [mov.raw_id] }
                              openExportModal(selection)
                            }}
                            className="bg-transparent rounded p-1.5 text-table-foreground/70 transition-colors hover:bg-table-foreground/10 hover:text-table-foreground ml-1"
                            title="Exportar PDF"
                          >
                            <Printer className="h-4 w-4 text-blue-400" />
                          </Button>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="px-4 py-16 text-center">
                      <ArrowRightLeft className="mx-auto mb-3 h-10 w-10 text-table-foreground/30" />
                      <p className="text-sm font-medium text-table-foreground">
                        No se encontraron transferencias
                      </p>
                      <p className="mt-1 text-xs text-table-foreground/70">
                        Ajusta los filtros o el rango de fechas
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pagination.total > 0 && (
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={pagination.perPage}
              currentPage={pagination.currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {/* Modal de detalle */}
      <TransferDetailModal
        transfer={selectedTransfer}
        onClose={() => setSelectedTransfer(null)}
      />

      {/* Modal de exportación */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => {
          setIsExportModalOpen(false)
          setExportSelection(null)
        }}
        onExport={handleExport}
        title="Exportar Transferencia"
        allowedFormats={['pdf']}
      />
    </div>
  )
}
